import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { CompanyPicker } from "@/components/sales/CompanyPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LABELS = { name: "Ragione sociale", trade_name: "Insegna", first_name: "Nome", last_name: "Cognome", vat_number: "P.IVA", tax_code: "Codice fiscale", sdi_code: "Codice SDI",
  pec: "PEC", website: "Sito", phone: "Telefono", email: "Email", category: "Categoria", source: "Origine", sector: "Settore", address: "Indirizzo", zip_code: "CAP",
  city: "Città", province: "Provincia", region: "Regione", country: "Paese", owner_id: "Responsabile" };
const COLL = { contacts: "referenti", opportunities: "opportunità", quotes: "offerte", orders: "ordini", contracts: "contratti", contract_fees: "canoni", invoices: "fatture",
  activities: "attività", interventions: "interventi", licenses: "licenze", assets: "asset", documents: "documenti", timeline_events: "eventi di cronologia",
  hour_packages: "pacchetti ore", leads: "lead", supplier_invoices: "fatture fornitori", ddt: "DDT", proforma: "pro forma" };

const FieldRow = ({ f, pick, setPick }) => (
  <tr className="border-t border-slate-100 text-xs" data-testid={`merge-field-${f.field}`}>
    <td className="py-1.5 pr-2 font-semibold text-slate-600">{LABELS[f.field] || f.field}</td>
    {["target", "source"].map((side) => (
      <td key={side} className="py-1.5 pr-2">
        {f.conflict ? <label className="flex items-start gap-1.5"><input type="radio" checked={(pick[f.field] || "target") === side} onChange={() => setPick({ ...pick, [f.field]: side })} data-testid={`merge-pick-${f.field}-${side}`} /><span>{f[side]}</span></label>
          : <span className={f[side] ? "" : "text-slate-400"}>{f[side] || "—"}</span>}
      </td>))}
  </tr>
);

export const MergeCompanyDialog = ({ open, onOpenChange, company }) => {
  const nav = useNavigate();
  const [src, setSrc] = useState(null);
  const [prev, setPrev] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setSrc(null); setPrev(null); setPick({}); } }, [open]);
  useEffect(() => {
    setPrev(null); setPick({});
    if (src?.id) api.get(`/crm/companies/${company.id}/merge-preview`, { params: { source_id: src.id } }).then((r) => setPrev(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [src, company.id]);
  const merge = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/companies/${company.id}/merge`, { source_id: src.id, take_from_source: Object.keys(pick).filter((k) => pick[k] === "source") });
      toast.success(`«${src.name}» unita in questa anagrafica`); onOpenChange(false); nav(0);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const counts = prev ? Object.entries(prev.counts) : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="merge-company-dialog">
        <DialogHeader><DialogTitle>Unisci anagrafiche</DialogTitle>
          <DialogDescription>Resta <b>{company.name}</b>. L'anagrafica scelta viene archiviata e tutto ciò che contiene (referenti, opportunità, offerte, ordini, contratti, fatture, attività…) passa qui.</DialogDescription></DialogHeader>
        <CompanyPicker value={src} onChange={setSrc} testId="merge-company-picker" />
        {prev && <>
          <div className="rounded-control bg-slate-50 p-3 text-xs text-slate-700" data-testid="merge-counts">
            <b>Verranno spostati:</b> {counts.length ? counts.map(([k, n]) => `${n} ${COLL[k] || k}`).join(" · ") : "nessun record collegato"}
          </div>
          <table className="w-full"><thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400"><th className="pb-1">Dato</th><th className="pb-1">Resta: {prev.target.name}</th><th className="pb-1">Da unire: {prev.source.name}</th></tr></thead>
            <tbody>{prev.fields.map((f) => <FieldRow key={f.field} f={f} pick={pick} setPick={setPick} />)}</tbody></table>
          <p className="text-[11px] text-slate-500">I dati vuoti vengono completati con quelli dell'altra anagrafica; dove sono diversi scegli quale tenere. Note, tag e sedi vengono sommati.</p>
        </>}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button disabled={!prev || busy} onClick={merge} data-testid="merge-confirm">{busy ? "Unione…" : "Unisci"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
